import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

//libraries
import { ClipLoader } from "react-spinners";

//components
import Label from "components/UI/Label";
import AuthInput from "components/UI/Inputs/AuthInput";

//utils
import { toast } from "sonner";
import instance from "lib/axiosInterceptor";
import { getUserDetails } from "utils/localStorageServices";
import useDocumentTitle from "hooks/useDocumentTitle";
import AuthPageWrapper, { AuthPageWrapperModal } from "../auth-page-wrapper";
import { ArrowRightIcon } from "assets/icons";
import ResendTimer from "components/UI/Timer/ResendTimer";
import {
  useLogin,
  useValidateOTP,
  useLoginWithOTP,
  useEmailVerification,
} from "hooks/query/useAccount";
import {
  LoginServicePayload,
  ValidateOTPServicePayload,
  LoginWithOTPServicePayload,
  EmailVerificaitonServicePayload,
  EmailVerifcationServiceResponse,
} from "interfaces";
import { persistAuthSession } from "utils/authSession";
import { setAuthCookies } from "utils/cookieService";
import { startDashboardOnboardingLoginSession } from "utils/dashboardOnboardingSession";
import { validateToken } from "utils/checkAuthorization";

// Type definitions
interface FormData {
  email: string;
  password: string;
}

interface ProfileData {
  email: string;
  phoneNumber: string | null;
  fullName: string;
  dateRegistered: string;
  lastLoginDate: string;
  jobTitle: string;
  jobId: string;
  linkedIn: string | null;
  location: string;
  locationShort: string | null;
}

interface ProfileResponse {
  data: ProfileData;
}

interface AuthUrlParams {
  redirect: string | null;
  app: string | null;
  meetingId: string | null;
}

interface PasswordInputFlowProps {
  verifyEmail: (e: React.FormEvent<HTMLFormElement>) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isButtonActive: boolean;
  isLoading: boolean;
  email: string;
  isModal?: boolean;
  handleClose?: (shouldOpenOnboardingModal?: boolean) => void;
  handleCallbackForLogout?: () => void;
}

interface OTPInputFlowProps {
  email: string;
  reference: string;
  isLoading: boolean;
  updateFlowInUrl: (flow: AuthFlow) => void;
  JobTitle: () => Promise<ProfileResponse>;
  newAccount: boolean;
  hasPassword: boolean;
  setEmailVerificationResponse: React.Dispatch<
    React.SetStateAction<EmailVerifcationServiceResponse | null>
  >;
  isModal?: boolean;
  handleClose?: (shouldOpenOnboardingModal?: boolean) => void;
  handleCallbackForLogout?: () => void;
}

type AuthFlow = "email" | "otp" | "password";

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// LocalStorage key for auth response
const AUTH_RESPONSE_KEY = "auth_verification_response";

// OTP resend cooldown (must match ResendTimer usage)
const OTP_COOLDOWN_SECONDS = 180;

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage write failures in restricted iframe contexts.
  }
};

const safeLocalStorageRemove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage remove failures in restricted iframe contexts.
  }
};

const safeSessionStorageSet = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage write failures in restricted iframe contexts.
  }
};

const getSafeRedirectPath = (value?: string | null): string | null => {
  if (!value) return null;

  let decoded = value.trim();
  // Defensively decode multiple times to support nested redirect encoding
  // patterns that can happen through chained loader redirects.
  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }

  // Only allow internal relative routes to prevent open redirects.
  if (!decoded.startsWith("/") || decoded.startsWith("//")) {
    return null;
  }

  return decoded;
};

const resolveFinalRedirectPath = (value?: string | null): string | null => {
  let current = getSafeRedirectPath(value);
  if (!current) return null;

  // Unwrap nested redirect chains like:
  // /login?redirect=%2Flogin%3Fredirect%3D%252Fmeet-addon%252Fmain-stage...
  for (let i = 0; i < 5; i += 1) {
    try {
      const parsed = new URL(current, window.location.origin);
      const isLoginRoute = parsed.pathname === "/login";
      const nested = parsed.searchParams.get("redirect");
      if (!isLoginRoute || !nested) break;

      const next = getSafeRedirectPath(nested);
      if (!next || next === current) break;
      current = next;
    } catch {
      break;
    }
  }

  return current;
};

// Write (or refresh) the per-email OTP timer so ResendTimer picks it up immediately
const writeOtpTimer = (email: string): void => {
  const expiryTime = Math.floor(Date.now() / 1000) + OTP_COOLDOWN_SECONDS;
  safeLocalStorageSet(`otp_resend_timer_${email}`, expiryTime.toString());
};

// Full cleanup on login success or modal close — removes auth response + OTP timer for that email
const clearAuthLocalStorage = (email: string): void => {
  safeLocalStorageRemove(AUTH_RESPONSE_KEY);
  safeLocalStorageRemove(`otp_resend_timer_${email}`);
};

// Partial cleanup used when going "back to email" — preserves per-email OTP timers so the
// countdown can resume if the user returns to the same email within the cooldown window
const clearAuthResponse = (): void => {
  safeLocalStorageRemove(AUTH_RESPONSE_KEY);
};

// Shared post-login success handler for both OTP and password flows
async function handleLoginSuccess({
  data,
  email,
  context,
  JobTitle,
  navigate,
  isModal,
  handleClose,
  handleCallbackForLogout,
  urlParams,
}: {
  data: any;
  email: string;
  context: "otp" | "password";
  JobTitle: () => Promise<ProfileResponse>;
  navigate: ReturnType<typeof useNavigate>;
  isModal?: boolean;
  handleClose?: (shouldOpenOnboardingModal?: boolean) => void;
  handleCallbackForLogout?: () => void;
  urlParams?: AuthUrlParams;
}) {
  const resolvedUrlParams: AuthUrlParams = (() => {
    if (urlParams && (urlParams.redirect || urlParams.app || urlParams.meetingId)) {
      return urlParams;
    }

    const search = new URLSearchParams(window.location.search);
    return {
      redirect: search.get("redirect"),
      app: search.get("app"),
      meetingId: search.get("meetingId"),
    };
  })();

  clearAuthLocalStorage(email);

  // Persist the session (cookies + localStorage + onboarding session).
  persistAuthSession(data);
  // Store user data in cookies (for cross-subdomain auth)
  try {
    setAuthCookies(data);
  } catch (error) {
    console.warn("Unable to persist auth cookies in this context:", error);
  }

  // Also store in localStorage for backward compatibility
  safeLocalStorageSet("email", data.email);
  safeLocalStorageSet("user", JSON.stringify(data));
  safeLocalStorageSet("token", data.token);
  startDashboardOnboardingLoginSession(data.email);

  // Password-specific: Zoom app redirect
  if (context === "password" && resolvedUrlParams.app === "zoom") {
    const cookieValue = `presenta-token=${data.token}; Path=/; domain=.thePresenta.com; SameSite=None; Secure`;
    document.cookie = cookieValue;
    alert(cookieValue);
    window.location.replace(`${resolvedUrlParams.redirect}`);
    return;
  }

  // Check if user needs onboarding
  const redirectPath = resolveFinalRedirectPath(resolvedUrlParams.redirect);
  const isMeetAddonRedirect = !!redirectPath?.startsWith("/meet-addon/");
  if (isModal && handleClose) {
    safeLocalStorageSet("onboardingComplete", "true");
    handleCallbackForLogout?.();
    handleClose(false);
  } else if (isMeetAddonRedirect) {
    // Meet add-on iframe must return directly to the requested frame route.
    safeLocalStorageSet("onboardingComplete", "true");
    window.location.replace(redirectPath!);
    return;
  } else {
    try {
      const onboardingData = await JobTitle();
      console.log("Onboarding Data:", onboardingData);
      if (onboardingData && onboardingData.data.jobTitle === "") {
        safeLocalStorageSet("onboardingComplete", "false");
        const countryData = btoa(JSON.stringify({ country: data.country }));
        safeSessionStorageSet("navigating-to-onboarding", "true");
        navigate(`/onboarding?c=${countryData}`);
      } else {
        safeLocalStorageSet("onboardingComplete", "true");
        navigate(redirectPath || "/overview");
      }
    } catch (error) {
      console.error("Error fetching job title:", error);
      navigate("/login");
    }
  }
}

// Flow encoding/decoding functions
const encodeFlow = (flow: AuthFlow): string => {
  const flowMap: Record<AuthFlow, string> = {
    email: btoa("e"),
    otp: btoa("o"),
    password: btoa("p"),
  };
  return flowMap[flow];
};

const decodeFlow = (encoded: string): AuthFlow | null => {
  const flowMap: Record<string, AuthFlow> = {
    [btoa("e")]: "email",
    [btoa("o")]: "otp",
    [btoa("p")]: "password",
  };
  return flowMap[encoded] || null;
};

export default function NewLogin({
  isModal,
  open,
  handleClose,
  handleCallbackForLogout,
}: {
  open?: boolean;
  isModal?: boolean;
  handleClose?: (shouldOpenOnboardingModal?: boolean) => void;
  handleCallbackForLogout?: () => void;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [urlParams, setUrlParams] = useState({});

  //usestate
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });
  const [flow, setFlow] = useState<AuthFlow>("email");
  const [isButtonActive, setIsButtonActive] = useState<boolean>(false);
  const [emailVerificationResponse, setEmailVerificationResponse] =
    useState<EmailVerifcationServiceResponse | null>(null);

  useDocumentTitle("StagePanda - Login", !isModal);

  // Wrapper function to clear URL params before closing modal
  const handleCloseWithCleanup = (
    shouldOpenOnboardingModal: boolean = false,
  ) => {
    if (handleClose) {
      // Clear the "s" parameter from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("s");
      setSearchParams(newParams);

      // Call original handleClose
      handleClose(shouldOpenOnboardingModal);

      // clear Auth-related localStorage items
      if (formData?.email) {
        clearAuthLocalStorage(formData.email);
      }

      // Reset flow to email for next open
      setFlow("email");

      setFormData({ email: "", password: "" });
    }
  };

  // TanStack Query hook for email verification
  const { mutate: verifyEmailMutation, isPending: isLoading } =
    useEmailVerification((data) => {
      // Save to localStorage
      safeLocalStorageSet(AUTH_RESPONSE_KEY, JSON.stringify(data));

      setEmailVerificationResponse(data);

      if (data.hasPassword) {
        updateFlowInUrl("password");
      } else {
        writeOtpTimer(formData.email);
        updateFlowInUrl("otp");
        toast.success(`OTP sent to ${formData.email}`);
      }
    });

  useEffect(() => {
    const getUrlParam = new URLSearchParams(window.location.search);
    setUrlParams({
      redirect: getUrlParam.get("redirect"),
      app: getUrlParam.get("app"),
      meetingId: getUrlParam.get("meetingId"),
    });
  }, []);

  // Hard fallback: if auth is already valid and a redirect target exists,
  // immediately return to that target (covers iframe/login-loop scenarios).
  useEffect(() => {
    const redirectPath = resolveFinalRedirectPath(searchParams.get("redirect"));
    if (!redirectPath) return;

    let isAuthenticated = false;
    try {
      const userDetails = getUserDetails();
      isAuthenticated = Boolean(userDetails?.token && validateToken());
    } catch {
      isAuthenticated = false;
    }

    if (isAuthenticated) {
      window.location.replace(redirectPath);
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    const { email } = formData;
    // Validate email format
    const isEmailValid = EMAIL_REGEX.test(email);
    setIsButtonActive(isEmailValid);
  }, [formData]);

  // Initialize flow from URL and load saved auth response on mount
  useEffect(() => {
    // Load saved auth response from localStorage
    const savedAuthResponse = safeLocalStorageGet(AUTH_RESPONSE_KEY);
    if (savedAuthResponse) {
      try {
        const parsedResponse: EmailVerifcationServiceResponse =
          JSON.parse(savedAuthResponse);
        setEmailVerificationResponse(parsedResponse);
        setFormData((prev) => ({ ...prev, email: parsedResponse.email }));
      } catch (error) {
        console.error("Error parsing saved auth response:", error);
        safeLocalStorageRemove(AUTH_RESPONSE_KEY);
      }
    }

    // Initialize flow from URL
    const encodedFlow = searchParams.get("s");
    if (encodedFlow) {
      const decodedFlow = decodeFlow(encodedFlow);
      if (decodedFlow) {
        setFlow(decodedFlow);
      }
    }

    // What does this do ????
    // setUrlParams({
    //   redirect: searchParams.get("redirect"),
    //   app: searchParams.get("app"),
    //   meetingId: searchParams.get("meetingId"),
    // });
  }, [searchParams]);

  const JobTitle = async (): Promise<ProfileResponse> => {
    const res = await instance.get<ProfileData>(`/Account/MyProfile`);
    return { data: res.data };
  };

  // Helper function to update flow in URL
  const updateFlowInUrl = (newFlow: AuthFlow): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("s", encodeFlow(newFlow));
    setSearchParams(newParams);
    setFlow(newFlow);
  };

  //functions
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    const userDetails = getUserDetails();
    if (userDetails) {
      // Handle user details
    }
  }, []);

  const verifyEmail = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (formData.email === "") {
      toast.error("Enter an email address");
      return;
    }

    if (!EMAIL_REGEX.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsButtonActive(false);

    const payload: EmailVerificaitonServicePayload = {
      email: formData.email,
    };

    verifyEmailMutation(payload, {
      onError: (error: any) => {
        const message = error?.response?.data?.message ?? error.message;
        toast.error(message);
      },
      onSettled: () => {
        setIsButtonActive(EMAIL_REGEX.test(formData.email));
      },
    });
  };

  if (isModal && open !== undefined && handleClose) {
    return (
      <AuthPageWrapperModal open={open} handleClose={handleCloseWithCleanup}>
        {flow === "email" ? (
          <EmailInputFlow
            verifyEmail={verifyEmail}
            handleInputChange={handleInputChange}
            isButtonActive={isButtonActive}
            isLoading={isLoading}
            email={formData.email}
            isModal={isModal}
            handleClose={handleCloseWithCleanup}
          />
        ) : flow === "otp" ? (
          <OTPInputFlow
            email={formData.email}
            reference={emailVerificationResponse?.reference || ""}
            isLoading={isLoading}
            updateFlowInUrl={updateFlowInUrl}
            JobTitle={JobTitle}
            newAccount={emailVerificationResponse?.newAccount || false}
            setEmailVerificationResponse={setEmailVerificationResponse}
            hasPassword={emailVerificationResponse?.hasPassword || false}
            isModal={isModal}
            handleClose={handleCloseWithCleanup}
            handleCallbackForLogout={handleCallbackForLogout}
          />
        ) : (
          <PasswordInputFlow
            email={formData.email}
            reference={emailVerificationResponse?.reference || ""}
            isLoading={isLoading}
            updateFlowInUrl={updateFlowInUrl}
            JobTitle={JobTitle}
            newAccount={emailVerificationResponse?.newAccount || false}
            setEmailVerificationResponse={setEmailVerificationResponse}
            hasPassword={emailVerificationResponse?.hasPassword || false}
            isModal={isModal}
            handleClose={handleCloseWithCleanup}
            handleCallbackForLogout={handleCallbackForLogout}
          />
        )}
      </AuthPageWrapperModal>
    );
  }

  return (
    <AuthPageWrapper>
      {flow === "email" ? (
        <EmailInputFlow
          verifyEmail={verifyEmail}
          handleInputChange={handleInputChange}
          isButtonActive={isButtonActive}
          isLoading={isLoading}
          email={formData.email}
        />
      ) : flow === "otp" ? (
        <OTPInputFlow
          email={formData.email}
          reference={emailVerificationResponse?.reference || ""}
          isLoading={isLoading}
          updateFlowInUrl={updateFlowInUrl}
          JobTitle={JobTitle}
          newAccount={emailVerificationResponse?.newAccount || false}
          setEmailVerificationResponse={setEmailVerificationResponse}
          hasPassword={emailVerificationResponse?.hasPassword || false}
        />
      ) : (
        <PasswordInputFlow
          email={formData.email}
          reference={emailVerificationResponse?.reference || ""}
          isLoading={isLoading}
          updateFlowInUrl={updateFlowInUrl}
          JobTitle={JobTitle}
          newAccount={emailVerificationResponse?.newAccount || false}
          setEmailVerificationResponse={setEmailVerificationResponse}
          hasPassword={emailVerificationResponse?.hasPassword || false}
        />
      )}
    </AuthPageWrapper>
  );
}

function EmailInputFlow({
  verifyEmail,
  handleInputChange,
  isButtonActive,
  isLoading,
  email,
}: PasswordInputFlowProps) {
  useEffect(() => {
    // Check localStorage for unAuthenticatedUserDetails - this is stored when the user joins an Event/Presentation
    // We then want to get their store info for a better UI
    const unAuthenticatedUserDetails = safeLocalStorageGet(
      "unAuthenticatedUserDetails",
    );
    if (unAuthenticatedUserDetails) {
      try {
        const userData = JSON.parse(unAuthenticatedUserDetails);
        if (userData.email && !email) {
          // Trigger a synthetic change event to update the email field
          const syntheticEvent = {
            target: {
              name: "email",
              value: userData.email,
            },
          } as React.ChangeEvent<HTMLInputElement>;
          handleInputChange(syntheticEvent);
        }
      } catch (error) {
        console.error("Error parsing unAuthenticatedUserDetails:", error);
      }
    }
  }, []);

  return (
    <section className="mt-[3%] w-full">
      <h1 className="font-bold lg:text-[24px] text-left text-[#4B5563] font-monaRegular">
        Signup or login
      </h1>

      <p className="text-[#6F7782] font-sfLight mt-3">
        Enter your email to get started. We'll recognize you if you already have
        an account.
      </p>

      <section className="mt-4">
        <form onSubmit={verifyEmail} className="">
          <div>
            <Label text="Email address" />
            <AuthInput
              type="email"
              name="email"
              placeholder="Enter your email address"
              onChange={handleInputChange}
              disabled={isLoading}
              value={email}
            />
          </div>

          <button
            disabled={!isButtonActive || isLoading}
            className={`w-full py-3 text-sm text-white bg-black rounded-2xl mt-10 font-monaSemiBold flex items-center justify-center gap-[10px] ${
              !isButtonActive || isLoading
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            type="submit"
          >
            {isLoading ? (
              <ClipLoader color="#FFFFFF" size={20} />
            ) : (
              <>
                Continue
                <ArrowRightIcon stroke="#FFFFFF" />
              </>
            )}
          </button>
        </form>

        <div className="flex items-center justify-center mt-4 gap-x-2">
          <p className="text-sm">
            😊 We'll create your account automatically if you're new.
          </p>
        </div>
      </section>
    </section>
  );
}

function OTPInputFlow({
  email,
  reference,
  updateFlowInUrl,
  JobTitle,
  newAccount,
  setEmailVerificationResponse,
  hasPassword,
  isModal,
  handleClose,
  handleCallbackForLogout,
}: OTPInputFlowProps) {
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [isOtpComplete, setIsOtpComplete] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // TanStack Query hook for OTP validation
  const { mutate: validateOTP, isPending: isSubmitting } = useValidateOTP(
    async (data) => {
      await handleLoginSuccess({
        data,
        email,
        context: "otp",
        JobTitle,
        navigate,
        isModal,
        handleClose,
        handleCallbackForLogout,
      });
    },
  );

  // TanStack Query hook for resending OTP
  const { mutate: resendOTP, isPending: isResending } = useLoginWithOTP(
    (data) => {
      // Update the reference in emailVerificationResponse
      setEmailVerificationResponse((prev) => {
        if (prev) {
          const updatedResponse = { ...prev, reference: data.code };
          // Save updated response to localStorage
          safeLocalStorageSet(
            AUTH_RESPONSE_KEY,
            JSON.stringify(updatedResponse),
          );
          return updatedResponse;
        }
        return prev;
      });

      toast.success("OTP has been resent to your email");

      // Clear existing OTP inputs
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    },
  );

  const RESEND_COOLDOWN = OTP_COOLDOWN_SECONDS;
  const STORAGE_KEY = `otp_resend_timer_${email}`;

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    const complete = otp.every((digit) => digit !== "");
    setIsOtpComplete(complete);
  }, [otp]);

  const handleChange = (index: number, value: string): void => {
    // Handle multi-character input (iOS paste via onChange)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6 - index);
      if (digits.length > 0) {
        const newOtp = [...otp];
        for (let i = 0; i < digits.length && index + i < 6; i++) {
          newOtp[index + i] = digits[i];
        }
        setOtp(newOtp);

        const nextIndex = Math.min(index + digits.length, 5);
        const nextEmptyIndex = newOtp.findIndex(
          (val, idx) => idx >= index && val === "",
        );
        const focusIndex = nextEmptyIndex === -1 ? nextIndex : nextEmptyIndex;
        inputRefs.current[focusIndex]?.focus();
      }
      return;
    }

    // Handle single character input
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ): void => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, 6);

    if (digits.length === 0) return;

    const newOtp = [...otp];

    // If pasting a full 6-digit code, fill from the beginning
    // Otherwise, fill starting from the current index
    const startIndex = digits.length >= 6 ? 0 : index;

    for (let i = 0; i < digits.length && startIndex + i < 6; i++) {
      newOtp[startIndex + i] = digits[i];
    }

    setOtp(newOtp);

    const nextEmptyIndex = newOtp.findIndex((val) => val === "");
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleResendOtp = async (): Promise<void> => {
    const payload: LoginWithOTPServicePayload = {
      username: email,
    };

    resendOTP(payload, {
      onError: (error: any) => {
        const message = error?.response?.data?.message ?? error.message;
        toast.error(message || "Failed to resend OTP");
        throw error; // Re-throw so ResendTimer knows it failed
      },
    });
  };

  const handleSubmit = (): void => {
    if (!isOtpComplete) {
      toast.error("Please enter the complete 6-digit OTP");
      return;
    }

    const otpCode = otp.join("");

    const payload: ValidateOTPServicePayload = {
      username: email,
      reference: reference,
      otp: otpCode,
    };

    validateOTP(payload, {
      onError: (error: any) => {
        const message = error?.response?.data?.message ?? error.message;
        toast.error(message);
      },
    });
  };

  const maskEmail = (email: string): string => {
    const [localPart, domain] = email.split("@");
    if (localPart.length <= 4) {
      return `${localPart.charAt(0)}***@${domain}`;
    }
    const visibleStart = localPart.substring(0, 3);
    const visibleEnd = localPart.substring(localPart.length - 4);
    return `${visibleStart}****${visibleEnd}@${domain}`;
  };

  return (
    <section className="w-full">
      {/* Title */}
      <div className="flex justify-between w-full">
        <h1 className="font-bold lg:text-[24px] text-left text-[#4B5563] font-monaRegular">
          OTP
        </h1>
        {!newAccount && hasPassword && (
          <button
            onClick={() => updateFlowInUrl("password")}
            disabled={isSubmitting}
            className="h-[28px] bg-[#F5F5F5] border border-[#DFDFDF] flex items-center justify-center px-4 rounded-lg gap-2 font-sfMedium font-medium text-sm text-text-color-700 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M8.00032 1.90863C11.4059 1.90881 14.1663 4.66997 14.1663 8.07562C14.1662 11.4811 11.4058 14.2415 8.00032 14.2416C4.59467 14.2416 1.8335 11.4812 1.83333 8.07562C1.83333 4.66987 4.59456 1.90863 8.00032 1.90863Z"
                fill="#444D5A"
                stroke="#DFDFDF"
              />
              <path
                d="M10.5 7.22861V6.74194C10.5 6.12194 10.5 4.24194 8 4.24194C5.5 4.24194 5.5 6.12194 5.5 6.74194V7.22861C4.68667 7.40861 4.41333 7.93528 4.41333 9.07528V9.74194C4.41333 11.2086 4.86667 11.6619 6.33333 11.6619H9.66667C11.1333 11.6619 11.5867 11.2086 11.5867 9.74194V9.07528C11.5867 7.93528 11.3133 7.40861 10.5 7.22861ZM8 10.1419C7.59333 10.1419 7.26668 9.81528 7.26668 9.40861C7.26668 9.00194 7.59333 8.67527 8 8.67527C8.40667 8.67527 8.73332 9.00194 8.73332 9.40861C8.73332 9.81528 8.40667 10.1419 8 10.1419ZM9.5 7.15527H6.5V6.74194C6.5 5.76861 6.74 5.24194 8 5.24194C9.26 5.24194 9.5 5.76861 9.5 6.74194V7.15527Z"
                fill="#444D5A"
              />
            </svg>
            Sign in with password
          </button>
        )}
      </div>

      <p className="text-[#6F7782] font-sfLight mt-3 text-sm">
        Please enter the 6 digit code we sent to {email}
      </p>

      <p className="text-[#6F7782] font-sfLight opacity-0">
        Enter your email to get started. We'll recognize you if you already have
        an account.
      </p>

      {/* OTP Input Fields with spacing */}
      <div className="flex gap-2 mb-8 justify-center mt-4">
        {otp.map((digit: string, index: number) => (
          <React.Fragment key={index}>
            <input
              ref={(el: HTMLInputElement | null) =>
                (inputRefs.current[index] = el)
              }
              type="text"
              inputMode="numeric"
              value={digit}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange(index, e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(index, e)
              }
              onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                handlePaste(index, e)
              }
              autoComplete="one-time-code"
              disabled={isSubmitting}
              className={`w-10 md:w-[58.3px] h-12 text-center text-xl text-text-color-300 font-semibold bg-[#F5F5F5] rounded-lg focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                digit ? "border border-[#FF6929]" : "border border-[#F7F7F7]"
              }`}
              aria-label={`OTP digit ${index + 1}`}
            />
            {/* Add extra spacing after the 3rd input */}
            {index === 2 && <div className="w-4"></div>}
          </React.Fragment>
        ))}
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!isOtpComplete || isSubmitting}
        className={`w-full py-3 text-sm text-white bg-black rounded-2xl font-monaSemiBold flex items-center justify-center gap-[10px] hover:bg-gray-800 transition-colors ${
          !isOtpComplete || isSubmitting ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {isSubmitting ? (
          <ClipLoader color="#FFFFFF" size={20} />
        ) : (
          <>
            Sign in
            <ArrowRightIcon stroke="#FFFFFF" />
          </>
        )}
      </button>

      {/* Back + Resend OTP */}
      <div className="mt-4 flex items-center justify-center gap-x-4">
        <button
          onClick={() => {
            clearAuthLocalStorage(email);
            updateFlowInUrl("email");
          }}
          disabled={isSubmitting}
          className="text-sm text-[#FF6B35] font-sfMedium hover:text-[#E55A25] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back to email
        </button>
        <ResendTimer
          storageKey={STORAGE_KEY}
          cooldownSeconds={RESEND_COOLDOWN}
          onResend={handleResendOtp}
          isResending={isResending}
          isDisabled={isSubmitting}
        />
      </div>
    </section>
  );
}

function PasswordInputFlow({
  email,
  updateFlowInUrl,
  JobTitle,
  newAccount,
  setEmailVerificationResponse,
  isModal,
  handleClose,
  handleCallbackForLogout,
}: OTPInputFlowProps) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [urlParams, setUrlParams] = useState<{
    redirect: string | null;
    app: string | null;
    meetingId: string | null;
  }>({
    redirect: "",
    app: "",
    meetingId: "",
  });

  useEffect(() => {
    const getUrlParam = new URLSearchParams(window.location.search);
    setUrlParams({
      redirect: getUrlParam.get("redirect"),
      app: getUrlParam.get("app"),
      meetingId: getUrlParam.get("meetingId"),
    });
  }, []);

  // TanStack Query hook for login
  const { mutate: login, isPending: isSubmitting } = useLogin(async (data) => {
    await handleLoginSuccess({
      data,
      email,
      context: "password",
      JobTitle,
      navigate,
      isModal,
      handleClose,
      handleCallbackForLogout,
      urlParams,
    });
  });

  // TanStack Query hook for switching to OTP flow
  const { mutate: requestOTP } = useLoginWithOTP((data) => {
    // Update the reference in emailVerificationResponse
    setEmailVerificationResponse((prev) => {
      if (prev) {
        const updatedResponse = {
          ...prev,
          reference: data.code,
        };
        // Save updated response to localStorage
        safeLocalStorageSet(
          AUTH_RESPONSE_KEY,
          JSON.stringify(updatedResponse),
        );
        return updatedResponse;
      }
      return prev;
    });

    writeOtpTimer(email);
    updateFlowInUrl("otp");
  });

  const handleInputChange = (e: any) => {
    const { value } = e.target;
    setPassword(value);
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    const payload: LoginServicePayload = {
      username: email,
      password: password,
    };

    login(payload, {
      onError: (error: any) => {
        const message = error?.response?.data?.message ?? error.message;
        toast.error(message);
      },
    });
  };

  function handleSetOTPFlow() {
    if (newAccount) {
      updateFlowInUrl("otp");
    } else {
      const payload: LoginWithOTPServicePayload = {
        username: email,
      };

      requestOTP(payload, {
        onError: (error: any) => {
          const message = error?.response?.data?.message ?? error.message;
          toast.error(message);
        },
      });
    }
  }

  return (
    <section className="w-full">
      {/* Title */}
      <div className="flex justify-between w-full">
        <h1 className="font-bold lg:text-[24px] text-left text-[#4B5563] font-monaRegular">
          Password
        </h1>

        <button
          onClick={() => handleSetOTPFlow()}
          disabled={isSubmitting}
          className="h-[28px] bg-[#F5F5F5] border border-[#DFDFDF] flex items-center justify-center px-4 rounded-lg gap-2 font-sfMedium font-medium text-sm text-text-color-700 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask id="path-1-inside-1_99_1848" fill="white">
              <path d="M10 14.742C9.72667 14.742 9.5 14.5153 9.5 14.242V1.90863C9.5 1.6353 9.72667 1.40863 10 1.40863C10.2733 1.40863 10.5 1.6353 10.5 1.90863V14.242C10.5 14.5153 10.2733 14.742 10 14.742Z" />
            </mask>
            <path
              d="M10 14.742C9.72667 14.742 9.5 14.5153 9.5 14.242V1.90863C9.5 1.6353 9.72667 1.40863 10 1.40863C10.2733 1.40863 10.5 1.6353 10.5 1.90863V14.242C10.5 14.5153 10.2733 14.742 10 14.742Z"
              fill="#444D5A"
            />
            <path
              d="M10 14.742V13.742C10.279 13.742 10.5 13.963 10.5 14.242H9.5H8.5C8.5 15.0676 9.17438 15.742 10 15.742V14.742ZM9.5 14.242H10.5V1.90863H9.5H8.5V14.242H9.5ZM9.5 1.90863H10.5C10.5 2.18758 10.279 2.40863 10 2.40863V1.40863V0.40863C9.17438 0.40863 8.5 1.08301 8.5 1.90863H9.5ZM10 1.40863V2.40863C9.72105 2.40863 9.5 2.18758 9.5 1.90863H10.5H11.5C11.5 1.08301 10.8256 0.40863 10 0.40863V1.40863ZM10.5 1.90863H9.5V14.242H10.5H11.5V1.90863H10.5ZM10.5 14.242H9.5C9.5 13.963 9.72105 13.742 10 13.742V14.742V15.742C10.8256 15.742 11.5 15.0676 11.5 14.242H10.5Z"
              fill="#676767"
              mask="url(#path-1-inside-1_99_1848)"
            />
            <path
              opacity="0.4"
              d="M4.33362 3.24194H7.83362V12.9089H4.33362C3.13643 12.9089 2.16663 11.9391 2.16663 10.7419V5.40894C2.16663 4.21174 3.13643 3.24194 4.33362 3.24194Z"
              fill="#444D5A"
              stroke="#676767"
            />
            <path
              opacity="0.4"
              d="M12 13.4086H10V2.74194H12C13.4733 2.74194 14.6667 3.93528 14.6667 5.40861V10.7419C14.6667 12.2153 13.4733 13.4086 12 13.4086Z"
              fill="#444D5A"
            />
            <path
              d="M3.83329 8.74194C3.74663 8.74194 3.65996 8.72194 3.57996 8.68861C3.49996 8.65527 3.42663 8.60861 3.35996 8.54861C3.29996 8.48194 3.25329 8.40861 3.21329 8.32861C3.17996 8.24861 3.16663 8.16194 3.16663 8.07527C3.16663 7.90194 3.23996 7.72861 3.35996 7.60194C3.39329 7.57527 3.42663 7.54861 3.45996 7.52194C3.49996 7.49527 3.53996 7.47527 3.57996 7.46194C3.61996 7.44194 3.65996 7.42861 3.69996 7.42194C3.92663 7.37527 4.15329 7.44861 4.30663 7.60194C4.42663 7.72861 4.49996 7.90194 4.49996 8.07527C4.49996 8.16194 4.47996 8.24861 4.44663 8.32861C4.41329 8.40861 4.36663 8.48194 4.30663 8.54861C4.23996 8.60861 4.16663 8.65527 4.08663 8.68861C4.00663 8.72194 3.91996 8.74194 3.83329 8.74194Z"
              fill="#444D5A"
            />
            <path
              d="M6.16667 8.74196C6.08 8.74196 5.99333 8.72196 5.91333 8.68863C5.83333 8.6553 5.76 8.60863 5.69333 8.54863C5.56667 8.42196 5.5 8.2553 5.5 8.0753C5.5 7.98863 5.52 7.90196 5.55333 7.82196C5.58667 7.7353 5.63333 7.66863 5.69333 7.60196C5.94 7.35529 6.38667 7.35529 6.64 7.60196C6.76 7.72863 6.83333 7.90196 6.83333 8.0753C6.83333 8.16196 6.81333 8.24863 6.78 8.32863C6.74667 8.40863 6.7 8.48196 6.64 8.54863C6.57333 8.60863 6.5 8.6553 6.42 8.68863C6.34 8.72196 6.25333 8.74196 6.16667 8.74196Z"
              fill="#444D5A"
            />
          </svg>
          Sign in with code
        </button>
      </div>

      <p className="text-[#6F7782] font-sfLight mt-3 text-sm">
        Please enter your password to sign in to your account.
      </p>

      <p className="text-[#6F7782] font-sfLight opacity-0">
        Enter your email to get started. We'll recognize you if you already have
        an account.
      </p>

      <section className="mt-4">
        <form onSubmit={handleSubmit}>
          <div>
            <Label text="Enter your password" />
            <AuthInput
              type="password"
              name="password"
              placeholder="**********"
              isPassword={true}
              onChange={(e) => handleInputChange(e)}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 text-sm text-white bg-black rounded-2xl font-monaSemiBold flex items-center justify-center gap-[10px] hover:bg-gray-800 transition-colors mt-10 ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? (
              <ClipLoader color="#FFFFFF" size={20} />
            ) : (
              <>
                Continue
                <ArrowRightIcon stroke="#FFFFFF" />
              </>
            )}
          </button>
        </form>

        <div className="flex items-center justify-between mt-1 gap-x-2 font-sfMedium text-sm">
          <button
            type="button"
            onClick={() => {
              clearAuthResponse();
              updateFlowInUrl("email");
            }}
            disabled={isSubmitting}
            className="text-[#FF6B35] hover:text-[#E55A25] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back to email
          </button>
          <Link to={"/forgot-password"}>
            <button className="text-[#5E5E5E]">Forgot password?</button>
          </Link>
        </div>
      </section>
    </section>
  );
}
